import React, { useState, useEffect, useRef } from 'react';
import { useContract } from '../hooks/useContract';
import { formatAddress } from '../utils/constants';
import './MatrixTree.css';

const MatrixTree = ({ userId, packageId, matrixData, network, onPackageChange, provider, signer }) => {
  const { 
    contract,
    getUserDetails,
    getMatrixData
  } = useContract(provider, signer, network);

  const [treeData, setTreeData] = useState({ root: null, children: [] });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [isVerySmall, setIsVerySmall] = useState(window.innerWidth <= 480);

  // LOOP PREVENTION
  const hasLoadedRef = useRef(false);
  const lastUserIdRef = useRef('');
  const lastPackageIdRef = useRef(1);

  // Handle resize
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
      setIsVerySmall(window.innerWidth <= 480);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Load matrix data - PREVENT LOOPS
  useEffect(() => {
    // Prevent loops and duplicate calls
    if (!userId || 
        !packageId || 
        !contract ||
        (userId === lastUserIdRef.current && 
         packageId === lastPackageIdRef.current && 
         hasLoadedRef.current)) {
      return;
    }

    // Only load if userId or packageId actually changed
    if (userId !== lastUserIdRef.current || packageId !== lastPackageIdRef.current) {
      console.log('Loading matrix data for user:', userId, 'package:', packageId);
      lastUserIdRef.current = userId;
      lastPackageIdRef.current = packageId;
      hasLoadedRef.current = true;
      
      loadMatrixData();
    }
  }, [userId, packageId, contract]); // MINIMAL dependencies

  const loadMatrixData = async () => {
    if (loading) return; // Prevent concurrent calls
    
    setLoading(true);
    setError(null);

    try {
      // Get matrix details first
      const matrixDetails = await getMatrixData(userId, packageId);
      const directDownlines = matrixDetails?.directDownlines || [];
      
      // Get root user details
      const rootDetails = await getUserDetails(userId);
      
      if (!rootDetails) {
        throw new Error('Failed to load user details');
      }

      const rootUser = {
        account: rootDetails.account,
        id: rootDetails.id,
        referrer: rootDetails.referrer,
        isActive: rootDetails.isActive,
        totalDirectReferrals: rootDetails.totalDirectReferrals,
        claimableAmount: rootDetails.claimableAmount,
        totalClaimed: rootDetails.totalClaimed,
        totalIncome: rootDetails.totalIncome
      };

      // Get children details (max 2)
      let children = [null, null];
      
      for (let i = 0; i < Math.min(2, directDownlines.length); i++) {
        const childId = directDownlines[i];
        if (childId && childId.toString() !== '0') {
          try {
            const childDetails = await getUserDetails(childId.toString());
            if (childDetails) {
              children[i] = {
                account: childDetails.account,
                id: childDetails.id,
                referrer: childDetails.referrer,
                isActive: childDetails.isActive,
                totalDirectReferrals: childDetails.totalDirectReferrals,
                claimableAmount: childDetails.claimableAmount,
                totalClaimed: childDetails.totalClaimed,
                totalIncome: childDetails.totalIncome
              };
            }
          } catch (err) {
            console.warn('Failed to load child:', childId.toString(), err);
          }
        }
      }

      setTreeData({ root: rootUser, children });
      console.log('Matrix data loaded successfully');
      
    } catch (error) {
      console.error('Error loading matrix data:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const TreeNode = ({ user, isRoot = false }) => {
    const getNodeClasses = () => {
      let classes = 'tree-node';
      if (isRoot) classes += ' root';
      else if (user) classes += ' user';
      else classes += ' empty';
      
      if (isVerySmall) classes += ' very-small';
      else if (isMobile) classes += ' mobile';
      
      return classes;
    };

    const getAvatarClasses = () => {
      let classes = 'node-avatar';
      if (user) classes += ' user';
      else classes += ' empty';
      
      if (isVerySmall) classes += ' very-small';
      else if (isMobile) classes += ' mobile';
      
      return classes;
    };

    const getTextClasses = (baseClass) => {
      let classes = baseClass;
      if (isRoot) classes += ' root';
      else if (user) classes += ' user';
      else classes += ' empty';
      
      if (isVerySmall) classes += ' very-small';
      else if (isMobile) classes += ' mobile';
      
      return classes;
    };

    const getStatusClasses = () => {
      let classes = 'node-status';
      if (user) {
        classes += user.isActive ? ' active' : ' inactive';
      } else {
        classes += ' vacant';
      }
      
      if (isVerySmall) classes += ' very-small';
      else if (isMobile) classes += ' mobile';
      
      return classes;
    };

    return (
      <div className={getNodeClasses()}>
        <div className={getAvatarClasses()}>
          {user ? (user.id ? user.id.toString().slice(-2) : '??') : '?'}
        </div>
        <div>
          <div className={getTextClasses('node-id')}>
            {user ? `ID: ${user.id || 'Unknown'}` : 'Empty Position'}
          </div>
          <div className={getTextClasses('node-address')}>
            {user ? formatAddress(user.account || '') : 'Available'}
          </div>
          <div className={getStatusClasses()}>
            {user ? (user.isActive ? 'Active' : 'Inactive') : 'Vacant'}
          </div>
        </div>
      </div>
    );
  };

  const getContainerClasses = () => {
    let classes = 'matrix-tree-container';
    if (isVerySmall) classes += ' very-small';
    else if (isMobile) classes += ' mobile';
    else classes += ' desktop';
    return classes;
  };

  const getStatsClasses = () => {
    let classes = 'matrix-stats';
    if (isVerySmall) classes += ' very-small';
    else if (isMobile) classes += ' mobile';
    return classes;
  };

  const getStatCardClasses = () => {
    let classes = 'matrix-stat-card';
    if (isVerySmall) classes += ' very-small';
    else if (isMobile) classes += ' mobile';
    return classes;
  };

  const getTreeClasses = () => {
    let classes = 'matrix-tree';
    if (isVerySmall) classes += ' very-small';
    else if (isMobile) classes += ' mobile';
    return classes;
  };

  const getChildrenClasses = () => {
    let classes = 'matrix-children';
    if (isVerySmall) classes += ' very-small';
    else if (isMobile) classes += ' mobile';
    return classes;
  };

  const handleRetry = () => {
    setError(null);
    hasLoadedRef.current = false; // Force reload
    loadMatrixData();
  };

  // Handle package change - PREVENT LOOPS
  const handlePackageChange = (e) => {
    const newPackage = parseInt(e.target.value);
    if (newPackage !== packageId) {
      hasLoadedRef.current = false; // Allow reload for new package
      if (onPackageChange) {
        onPackageChange(newPackage);
      }
    }
  };

  if (loading) {
    return (
      <div className={getContainerClasses()}>
        <div className="matrix-loading">
          <div className="loading-spinner"></div>
          <p>Loading matrix tree...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={getContainerClasses()}>
        <div className="matrix-error">
          <h4>Error Loading Matrix</h4>
          <p>{error}</p>
          <button 
            className="matrix-retry-btn"
            onClick={handleRetry}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={getContainerClasses()}>
      {/* Header with Package Dropdown */}
      <div className="matrix-header">
        <h3 className="matrix-title">Matrix Tree View</h3>
        <div className="matrix-package-dropdown">
          <label>Package:</label>
          <select 
            value={packageId || 1} 
            onChange={handlePackageChange}
            className="package-dropdown"
          >
            {Array.from({ length: 7 }, (_, i) => (
              <option key={i + 1} value={i + 1}>U{i + 1}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Stats */}
      <div className={getStatsClasses()}>
        <div className={getStatCardClasses()}>
          <div className={`matrix-stat-label${isVerySmall ? ' very-small' : isMobile ? ' mobile' : ''}`}>
            Package
          </div>
          <div className={`matrix-stat-value${isVerySmall ? ' very-small' : isMobile ? ' mobile' : ''}`}>
            U{packageId || 1}
          </div>
        </div>
        <div className={getStatCardClasses()}>
          <div className={`matrix-stat-label${isVerySmall ? ' very-small' : isMobile ? ' mobile' : ''}`}>
            Direct Members
          </div>
          <div className={`matrix-stat-value${isVerySmall ? ' very-small' : isMobile ? ' mobile' : ''}`}>
            {treeData.children.filter(child => child !== null).length}
          </div>
        </div>
        <div className={getStatCardClasses()}>
          <div className={`matrix-stat-label${isVerySmall ? ' very-small' : isMobile ? ' mobile' : ''}`}>
            Total Team
          </div>
          <div className={`matrix-stat-value${isVerySmall ? ' very-small' : isMobile ? ' mobile' : ''}`}>
            {matrixData?.totalTeamSize || '0'}
          </div>
        </div>
      </div>

      {/* Tree */}
      <div className={getTreeClasses()}>
        <TreeNode user={treeData.root} isRoot={true} />
        
        {treeData.root && (
          <div className={getChildrenClasses()}>
            <TreeNode user={treeData.children[0]} />
            <TreeNode user={treeData.children[1]} />
          </div>
        )}
      </div>

      {!treeData.root && !loading && !error && (
        <div className="matrix-no-data">
          <p>No matrix data available for this package.</p>
        </div>
      )}
    </div>
  );
};

export default MatrixTree;